import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import type { DayPart } from '@/models/account-management/DayPart';
import type { PlaylistSchedule } from '@/models/media-network/PlaylistSchedule';
import { minuteToTime } from '@/models/media-network/PlaylistSchedule';
import {
    addDays, assignLanes, monthGridDays, occursOn, resolveWindow, toYmd, weekDays, windowPieces,
} from '@/models/media-network/scheduleOccurrences';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';

export interface CalendarEntry {
    schedule: PlaylistSchedule;
    playlistId: string;
    playlistName: string;
}

interface Props {
    entries: CalendarEntry[];
    dayParts?: DayPart[];
    // Set when a location filter is applied, so the caption can say what is being shown.
    locationName?: string;
    onSelect: (schedule: PlaylistSchedule) => void;
}

type CalendarView = 'month' | 'week' | 'day';

// One colour per playlist, keyed by position in the list rather than hashed off the id: adjacent playlists
// are then guaranteed to differ, which a hash cannot promise and is the whole point of colouring them.
const PALETTE = [
    '#1f77b4', '#d62728', '#2ca02c', '#9467bd',
    '#ff7f0e', '#17becf', '#8c564b', '#e377c2',
];

const HOUR_HEIGHT = 44;
const DAY_HEIGHT = HOUR_HEIGHT * 24;
const GUTTER = 56;

// What is scheduled, as a month grid or an hour-by-hour week or day.
//
// A calendar is worth having here for ONE reason the list cannot serve: collisions. The month view answers
// "do these two both claim Tuesday"; the week and day views answer the sharper question, "do they both claim
// 8am" — which is the one that decides whether they actually compete. Overlapping blocks are laid out
// side by side rather than stacked, because a collision hidden behind another block is not a warning.
//
// It shows what is SCHEDULED, not what will play. At most one playlist is ever active for a location and app,
// and which one wins needs the location's timezone, its day parts and the precedence rules — all server-side.
// So overlapping blocks mean "these compete", not "these all run", and the caption says so.
const ScheduleCalendar = ({ entries, dayParts, locationName, onSelect }: Props) => {
    const strings = useLocalizedStrings();

    const today = new Date();
    const [view, setView] = useState<CalendarView>('month');
    // The anchor for whatever view is showing: a day inside the month, the week, or the day itself.
    const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), today.getDate()));

    const colourOf = useMemo(() => {
        const ids = [...new Set(entries.map((e) => e.playlistId))];
        return (playlistId: string) => PALETTE[Math.max(0, ids.indexOf(playlistId)) % PALETTE.length];
    }, [entries]);

    // Disabled schedules are dropped rather than dimmed: the question a calendar answers is what runs, and a
    // disabled schedule runs nowhere.
    const live = useMemo(() => entries.filter((e) => e.schedule.enabled), [entries]);

    const dayPartFor = (s: PlaylistSchedule) =>
        s.dayPartId ? dayParts?.find((d) => d.id === s.dayPartId) : undefined;

    const entriesOn = (day: Date) => live.filter((e) => occursOn(e.schedule, day));

    const step = (direction: number) => {
        if (view === 'month') return setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + direction, 1));
        setCursor(addDays(cursor, direction * (view === 'week' ? 7 : 1)));
    };

    const days = useMemo(() => {
        if (view === 'month') return monthGridDays(cursor.getFullYear(), cursor.getMonth());
        if (view === 'week') return weekDays(cursor);
        return [cursor];
    }, [view, cursor]);

    const heading = useMemo(() => {
        if (view === 'month') return cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
        if (view === 'day') {
            return cursor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        }
        const week = weekDays(cursor);
        const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
        return `${week[0].toLocaleDateString(undefined, opts)} – ${week[6].toLocaleDateString(undefined, { ...opts, year: 'numeric' })}`;
    }, [view, cursor]);

    // Short enough for a chip. A day part has no fixed hours until a location resolves it, so its NAME is
    // shown rather than hours presented as fact.
    const windowLabel = (s: PlaylistSchedule): string => {
        if (s.dayPartId) return dayPartFor(s)?.name ?? strings.playlistUnknownTarget;
        if (s.startMinute == null) return strings.playlistWindowAllDay;
        return `${minuteToTime(s.startMinute)}–${minuteToTime(s.endMinute)}`;
    };

    const tooltipFor = (e: CalendarEntry): string => {
        const part = dayPartFor(e.schedule);
        const hours = part
            ? `${part.name} (${minuteToTime(part.startMinute)}–${minuteToTime(part.endMinute)})`
            : e.schedule.startMinute == null
                ? strings.playlistWindowAllDay
                : `${minuteToTime(e.schedule.startMinute)}–${minuteToTime(e.schedule.endMinute)}`;
        return `${e.playlistName} · ${hours}`;
    };

    // Positioned blocks for one day of a time grid, including the tail of anything that started yesterday and
    // ran past midnight.
    const timedBlocksFor = (day: Date) => {
        const blocks = entriesOn(day).flatMap((e) => {
            const w = resolveWindow(e.schedule, dayPartFor(e.schedule));
            if (!w) return [];
            const { head } = windowPieces(w);
            return [{ entry: e, ...head, continues: w.endMinute < w.startMinute, isTail: false }];
        });

        // Yesterday's wrap, landing in the small hours of this one.
        const tails = entriesOn(addDays(day, -1)).flatMap((e) => {
            const w = resolveWindow(e.schedule, dayPartFor(e.schedule));
            if (!w) return [];
            const { tail } = windowPieces(w);
            return tail ? [{ entry: e, ...tail, continues: false, isTail: true }] : [];
        });

        return assignLanes([...tails, ...blocks]);
    };

    const allDayFor = (day: Date) =>
        entriesOn(day).filter((e) => !resolveWindow(e.schedule, dayPartFor(e.schedule)));

    const chipSx = (playlistId: string) => ({
        justifyContent: 'flex-start',
        height: 20,
        width: '100%',
        bgcolor: colourOf(playlistId),
        color: 'common.white',
        '& .MuiChip-label': {
            px: 0.75, fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis',
        },
        '&:hover': { bgcolor: colourOf(playlistId), filter: 'brightness(1.15)' },
    });

    const weekdayNames = strings.weekdayShort.split(',');
    const todayYmd = toYmd(today);

    return (
        <Stack spacing={1} sx={{ minHeight: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={view}
                    onChange={(_e, next: CalendarView | null) => { if (next) setView(next); }}
                >
                    <ToggleButton value="month">{strings.playlistCalendarMonth}</ToggleButton>
                    <ToggleButton value="week">{strings.playlistCalendarWeek}</ToggleButton>
                    <ToggleButton value="day">{strings.playlistCalendarDay}</ToggleButton>
                </ToggleButtonGroup>

                <IconButton size="small" aria-label="previous" onClick={() => step(-1)}>
                    <ChevronLeftIcon />
                </IconButton>
                <Typography variant="subtitle1" sx={{ minWidth: 220, textAlign: 'center' }}>{heading}</Typography>
                <IconButton size="small" aria-label="next" onClick={() => step(1)}>
                    <ChevronRightIcon />
                </IconButton>
                <Button
                    size="input"
                    onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), today.getDate()))}
                >
                    {strings.playlistCalendarToday}
                </Button>
            </Stack>

            {/* Said once, up front. Someone WILL open this to find out why a promo is not showing, and
                overlapping blocks would otherwise read as "all of these run". */}
            <Alert severity="info" variant="outlined">
                {locationName
                    ? strings.playlistCalendarScopedHelp.replace('{{location}}', locationName)
                    : strings.playlistCalendarHelp}
            </Alert>

            {view === 'month' ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                    {weekdayNames.map((d) => (
                        <Typography key={d} variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 0.5 }}>
                            {d}
                        </Typography>
                    ))}

                    {days.map((day) => {
                        const ymd = toYmd(day);
                        const inMonth = day.getMonth() === cursor.getMonth();

                        return (
                            <Paper
                                key={ymd}
                                variant="outlined"
                                sx={{
                                    p: 0.5,
                                    minHeight: 96,
                                    // Days either side of the month stay visible but recede — a schedule
                                    // running on the 1st usually started in the week before it.
                                    opacity: inMonth ? 1 : 0.45,
                                    borderColor: ymd === todayYmd ? 'primary.main' : undefined,
                                    borderWidth: ymd === todayYmd ? 2 : 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color={ymd === todayYmd ? 'primary.main' : 'text.secondary'}
                                    fontWeight={ymd === todayYmd ? 700 : 400}
                                    sx={{ cursor: 'pointer' }}
                                    // A date in the month is the natural way into that day's hours.
                                    onClick={() => { setCursor(day); setView('day'); }}
                                >
                                    {day.getDate()}
                                </Typography>

                                <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                                    {entriesOn(day).map((e) => (
                                        <Tooltip key={e.schedule.id} title={tooltipFor(e)}>
                                            <Chip
                                                size="small"
                                                label={`${windowLabel(e.schedule)} ${e.playlistName}`}
                                                onClick={() => onSelect(e.schedule)}
                                                sx={chipSx(e.playlistId)}
                                            />
                                        </Tooltip>
                                    ))}
                                </Stack>
                            </Paper>
                        );
                    })}
                </Box>
            ) : (
                <Stack spacing={0.5}>
                    {/* Day headers, aligned to the grid below by the same gutter the hour labels occupy. */}
                    <Box sx={{ display: 'flex', pl: `${GUTTER}px` }}>
                        {days.map((day) => (
                            <Box key={toYmd(day)} sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography
                                    variant="caption"
                                    color={toYmd(day) === todayYmd ? 'primary.main' : 'text.secondary'}
                                    fontWeight={toYmd(day) === todayYmd ? 700 : 400}
                                >
                                    {weekdayNames[day.getDay()]} {day.getDate()}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* All-day is a deliberate choice rather than missing times, so it gets its own row above
                        the hours instead of being stretched over all 24 of them. */}
                    {days.some((d) => allDayFor(d).length > 0) && (
                        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ width: GUTTER, pr: 1, textAlign: 'right', flexShrink: 0 }}
                            >
                                {strings.playlistWindowAllDay}
                            </Typography>
                            {days.map((day) => (
                                <Box key={toYmd(day)} sx={{ flex: 1, minWidth: 0, px: 0.25 }}>
                                    <Stack spacing={0.25}>
                                        {allDayFor(day).map((e) => (
                                            <Tooltip key={e.schedule.id} title={tooltipFor(e)}>
                                                <Chip
                                                    size="small"
                                                    label={e.playlistName}
                                                    onClick={() => onSelect(e.schedule)}
                                                    sx={chipSx(e.playlistId)}
                                                />
                                            </Tooltip>
                                        ))}
                                    </Stack>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box sx={{ maxHeight: '58vh', overflow: 'auto' }}>
                        <Box sx={{ display: 'flex', position: 'relative', height: DAY_HEIGHT }}>
                            {/* Hour labels, sitting on the line they name. */}
                            <Box sx={{ width: GUTTER, flexShrink: 0, position: 'relative' }}>
                                {Array.from({ length: 24 }, (_, h) => (
                                    <Typography
                                        key={h}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            position: 'absolute', top: h * HOUR_HEIGHT - 8, right: 8,
                                            fontSize: '0.68rem',
                                        }}
                                    >
                                        {minuteToTime(h * 60)}
                                    </Typography>
                                ))}
                            </Box>

                            {days.map((day) => {
                                const { laid, laneCount } = timedBlocksFor(day);
                                const isToday = toYmd(day) === todayYmd;

                                return (
                                    <Box
                                        key={toYmd(day)}
                                        sx={{
                                            flex: 1, minWidth: 0, position: 'relative',
                                            borderLeft: 1, borderColor: 'divider',
                                            bgcolor: isToday ? 'action.hover' : undefined,
                                        }}
                                    >
                                        {Array.from({ length: 24 }, (_, h) => (
                                            <Box
                                                key={h}
                                                sx={{
                                                    position: 'absolute', left: 0, right: 0, top: h * HOUR_HEIGHT,
                                                    borderTop: 1, borderColor: 'divider',
                                                    opacity: h % 6 === 0 ? 1 : 0.4,
                                                }}
                                            />
                                        ))}

                                        {laid.map((b) => (
                                            <Tooltip key={`${b.entry.schedule.id}-${b.isTail ? 'tail' : 'head'}`} title={tooltipFor(b.entry)}>
                                                <Box
                                                    onClick={() => onSelect(b.entry.schedule)}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: (b.startMinute / 1440) * DAY_HEIGHT,
                                                        height: Math.max(14, ((b.endMinute - b.startMinute) / 1440) * DAY_HEIGHT - 2),
                                                        left: `${(b.lane / laneCount) * 100}%`,
                                                        width: `${(1 / laneCount) * 100}%`,
                                                        px: 0.5, py: 0.25,
                                                        boxSizing: 'border-box',
                                                        bgcolor: colourOf(b.entry.playlistId),
                                                        color: 'common.white',
                                                        borderRadius: 1,
                                                        border: '1px solid rgba(255,255,255,0.35)',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        '&:hover': { filter: 'brightness(1.15)' },
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ fontSize: '0.68rem', display: 'block', lineHeight: 1.2 }}>
                                                        {b.entry.playlistName}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontSize: '0.62rem', opacity: 0.85 }}>
                                                        {/* The wrap is named on the block itself; a bar simply
                                                            stopping at midnight would read as ending there. */}
                                                        {b.isTail
                                                            ? `↳ ${minuteToTime(b.endMinute)}`
                                                            : `${minuteToTime(b.startMinute)}${b.continues ? ' →' : `–${minuteToTime(b.endMinute)}`}`}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        ))}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                </Stack>
            )}
        </Stack>
    );
};

export default ScheduleCalendar;
